import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShieldCheckIcon, CheckIcon } from '@heroicons/react/24/outline';
import { RoleService } from '../../../shared/services/roleService';
import { usePermission } from '../../../shared/hooks/usePermission';

const ACTION_LABELS = { create: 'Create', read: 'Read', update: 'Update', delete: 'Delete', export: 'Export' };
const ALL_ACTIONS = ['read', 'create', 'update', 'delete', 'export'];

export default function RoleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id && id !== 'add';
  const canEdit = usePermission('roles', isEdit ? 'update' : 'create');

  const [role, setRole] = useState({ name: '', description: '', permissions: {} });
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [modulesRes, roleRes] = await Promise.all([
          RoleService.getModules(),
          isEdit ? RoleService.getById(id) : Promise.resolve(null),
        ]);
        setModules(modulesRes.data?.data || []);
        if (roleRes) setRole(roleRes.data?.data || {});
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, isEdit]);

  const toggleAction = useCallback((moduleSlug, action) => {
    if (!canEdit) return;
    setRole((prev) => {
      const current = prev.permissions[moduleSlug] || [];
      const updated = current.includes(action)
        ? current.filter((a) => a !== action)
        : [...current, action];
      return {
        ...prev,
        permissions: updated.length
          ? { ...prev.permissions, [moduleSlug]: updated }
          : Object.fromEntries(Object.entries(prev.permissions).filter(([k]) => k !== moduleSlug)),
      };
    });
  }, [canEdit]);

  const toggleModule = useCallback((moduleSlug, actions) => {
    if (!canEdit) return;
    setRole((prev) => {
      const current = prev.permissions[moduleSlug] || [];
      const allSelected = actions.every((a) => current.includes(a));
      return {
        ...prev,
        permissions: allSelected
          ? Object.fromEntries(Object.entries(prev.permissions).filter(([k]) => k !== moduleSlug))
          : { ...prev.permissions, [moduleSlug]: [...actions] },
      };
    });
  }, [canEdit]);

  const handleSave = async () => {
    if (!role.name.trim()) { setError('Role name is required'); return; }
    setSaving(true);
    setError('');
    try {
      if (isEdit) {
        await RoleService.update(id, role);
      } else {
        await RoleService.create(role);
      }
      navigate('/admin/roles');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save role');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-8 text-center text-gray-400">Loading...</div>;

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <ShieldCheckIcon className="w-7 h-7 text-blue-600" />
        <h1 className="text-xl font-semibold text-gray-900">
          {isEdit ? 'Edit Role' : 'Create Role'}
        </h1>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg text-sm">{error}</div>
      )}

      {/* Basic info */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Role Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              value={role.name || ''}
              onChange={(e) => setRole((p) => ({ ...p, name: e.target.value }))}
              disabled={!canEdit}
              placeholder="e.g. Center Manager"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={role.description || ''}
              onChange={(e) => setRole((p) => ({ ...p, description: e.target.value }))}
              disabled={!canEdit}
              placeholder="Optional"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Permissions Matrix */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-sm font-semibold text-gray-800">Module Permissions</h2>
          <p className="text-xs text-gray-400 mt-0.5">Select what this role can do in each module</p>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left px-5 py-3 font-medium text-gray-600 w-48">Module</th>
                {ALL_ACTIONS.map((action) => (
                  <th key={action} className="text-center px-3 py-3 font-medium text-gray-600 w-20">
                    {ACTION_LABELS[action]}
                  </th>
                ))}
                <th className="text-center px-3 py-3 font-medium text-gray-600 w-20">All</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {modules.map((mod) => {
                const current = role.permissions?.[mod.slug] || [];
                const allSelected = mod.actions.every((a) => current.includes(a));
                return (
                  <tr key={mod.slug} className="hover:bg-gray-50/50">
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-800 capitalize">{mod.slug.replace(/-/g, ' ')}</span>
                        {mod.centerScoped && (
                          <span className="px-1.5 py-0.5 bg-amber-100 text-amber-600 text-xs rounded">center</span>
                        )}
                      </div>
                    </td>
                    {ALL_ACTIONS.map((action) => {
                      const supported = mod.actions.includes(action);
                      const checked = supported && current.includes(action);
                      return (
                        <td key={action} className="text-center px-3 py-3">
                          {supported ? (
                            <button
                              onClick={() => toggleAction(mod.slug, action)}
                              disabled={!canEdit}
                              className={`w-5 h-5 rounded border mx-auto flex items-center justify-center transition-colors ${
                                checked
                                  ? 'bg-blue-600 border-blue-600 text-white'
                                  : 'border-gray-300 hover:border-blue-400'
                              } disabled:opacity-50 disabled:cursor-not-allowed`}
                            >
                              {checked && <CheckIcon className="w-3 h-3" />}
                            </button>
                          ) : (
                            <span className="text-gray-200">—</span>
                          )}
                        </td>
                      );
                    })}
                    <td className="text-center px-3 py-3">
                      <button
                        onClick={() => toggleModule(mod.slug, mod.actions)}
                        disabled={!canEdit}
                        className={`w-5 h-5 rounded border mx-auto flex items-center justify-center transition-colors ${
                          allSelected
                            ? 'bg-blue-600 border-blue-600 text-white'
                            : 'border-gray-300 hover:border-blue-400'
                        } disabled:opacity-50 disabled:cursor-not-allowed`}
                      >
                        {allSelected && <CheckIcon className="w-3 h-3" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => navigate('/admin/roles')}
          className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        {canEdit && (
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving...' : isEdit ? 'Update Role' : 'Create Role'}
          </button>
        )}
      </div>
    </div>
  );
}
